import { styled, makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import {
  coachProfileBandOuter,
  coachProfileBandSectionGap,
  coachProfileContentCanvas,
  coachProfileSectionHeading,
  coachProfileTipSurface,
} from '@lib/pga-coach/coach-profile-layout'

export const Headline = styled(Typography)(() => ({
  fontSize: '20px',
}))

export const Series = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark,
}))

export const CardDate = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: theme.spacing(3),
  bottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    left: theme.spacing(2),
  },
}))

export const ContactInfoItem = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
}))

export const NamePicTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    width: '100%',
  },
  [theme.breakpoints.down(300)]: {
    '& div': {
      flexDirection: 'column',
    },
  },
}))

export const CrestImageWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse',
    '& p': {
      marginRight: 0,
      marginTop: theme.spacing(1),
    },
  },
}))

export const ContactInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '& span': {
    marginRight: theme.spacing(2),
    color: 'black',
  },
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
    '& > div': {
      alignItems: 'center',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),

      '& svg': {
        marginRight: '4px',
      },
    },
  },
}))

export const TitleRoundedBG = styled(Typography)(({ theme, bgcolor }) => ({
  display: 'inline-block',
  marginRight: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  fontSize: '0.75rem',
  lineHeight: '2',
  borderRadius: '1rem',
  textTransform: 'uppercase',
  fontWeight: 'bold',
  color: '#FFFFFF',
  backgroundColor: bgcolor,
}))

export const BioWrapper = styled(Box)(({ theme }) => ({
  color: 'black',

  '& .bioFigures': {
    marginRight: theme.spacing(2),
    width: '100%',
    minWidth: 0,
  },
}))

export const SocialIcon = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  width: '24px',
  height: '24px',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  backgroundColor: theme.palette.grey.light,
  borderRadius: '100%',
  fontSize: '12px',
  border: 'none',
  boxShadow: 'none',
  textDecoration: 'none',
  outline: 'none',
  '&:focus': {
    outline: 'none',
    boxShadow: 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.grey.dark,
  },
}))

export const FollowersAndSocial = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexWrap: 'wrap',
  width: '100%',
  gap: theme.spacing(6),

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
  },
}))

export const FollowersWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    textAlign: 'center',
  },
}))

export const SocialsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
  },
}))

export const FlexBox = styled(Box)(({ direction }) => ({
  display: 'flex',
  flexDirection: direction,
}))

export const useStyles = makeStyles((theme) => ({
  outerContainer: {
    /**
     * Wraps the two-column marketplace (sticky booking card left, content
     * right from md). Nested About bands live in the content column —
     * keep overflow visible so `position: sticky` on the card is not clipped.
     */
    backgroundColor: 'white',
    overflow: 'visible',
  },
  breadcrumbs: {
    marginTop: '6px',
  },
  profileHeaderDarkBand: {
    [theme.breakpoints.only('xs')]: {
      backgroundColor: '#0F2234',
      color: theme.palette.common.white,
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  profileHeaderRow: {
    width: '100%',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(2),
      alignItems: 'flex-start',
    },
  },
  contactInfoHero: {
    flexWrap: 'wrap',
    flex: '1 1 auto',
    minWidth: 0,
    gap: theme.spacing(2, 3),
    marginLeft: 0,
    paddingLeft: 0,
    textAlign: 'left',
    '&&': {
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    '& > div': {
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    [theme.breakpoints.only('xs')]: {
      flexDirection: 'row',
      width: '100%',
      gap: theme.spacing(1, 2),
      '&&': {
        justifyContent: 'center',
      },
    },
  },
  contactAndBookRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(1.5),
    width: '100%',
    marginTop: theme.spacing(1),
    [theme.breakpoints.only('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
  },
  contactHeroItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1.5),
    maxWidth: '100%',
  },
  contactHeroIconWell: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    color: theme.palette.primary.dark,
    '& .MuiSvgIcon-root': {
      fontSize: 30,
      display: 'block',
      [theme.breakpoints.down('sm')]: {
        fontSize: 26,
      },
    },
    [theme.breakpoints.only('xs')]: {
      color: theme.palette.gold.main,
    },
  },
  contactHeroText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    minWidth: 0,
    textAlign: 'left',
  },
  contactHeroValue: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.35,
    letterSpacing: '-0.01em',
    color: theme.palette.grey.darkest,
    textAlign: 'left',
    margin: 0,
    [theme.breakpoints.only('xs')]: {
      color: theme.palette.common.white,
    },
  },
  titleAndCrest: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 2),
    [theme.breakpoints.up('sm')]: {
      alignItems: 'baseline',
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
    [theme.breakpoints.only('xs')]: {
      justifyContent: 'center',
      textAlign: 'center',
    },
  },
  profileHeaderTitleBlock: {
    minWidth: 0,
    flex: '1 1 auto',
    [theme.breakpoints.only('xs')]: {
      textAlign: 'center',
      '& > *': {
        textAlign: 'center',
      },
    },
  },
  profileHeaderBookNow: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    '& .MuiButton-root': {
      marginLeft: 0,
    },
    [theme.breakpoints.only('xs')]: {
      width: '100%',
      marginLeft: 0,
      justifyContent: 'stretch',
      '& > a': {
        width: '100%',
        display: 'block',
      },
      '& .MuiButton-root': {
        width: '100%',
        paddingTop: theme.spacing(1.75),
        paddingBottom: theme.spacing(1.75),
        fontSize: '1rem',
        backgroundColor: theme.palette.blue.main,
        color: theme.palette.common.white,
        '&:hover': {
          backgroundColor: theme.palette.secondary.light,
          color: theme.palette.common.white,
        },
      },
    },
  },
  titleName: {
    fontFamily: theme.typography.h3.fontFamily,
    fontSize: 'clamp(2.25rem, 1.1vw + 1.25rem, 4rem)',
    color: 'black',
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
    marginTop: 0,
    marginBottom: 0,
    [theme.breakpoints.down('sm')]: {
      lineHeight: 1.12,
      fontSize: '2.75rem',
      letterSpacing: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    [theme.breakpoints.only('xs')]: {
      textAlign: 'center',
      color: theme.palette.common.white,
    },
  },
  titleCrest: {
    whiteSpace: 'nowrap',
  },
  bio: {
    marginBottom: '16px',
    '& p, & li': {
      whiteSpace: 'pre-wrap',
    },
    '& p, & ol, & ul': {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
    },
    '& p:first-child': {
      marginTop: 0,
    },
    '&, & p, & li': {
      fontSize: theme.typography.pxToRem(17),
      lineHeight: 1.6,
      [theme.breakpoints.up('md')]: {
        fontSize: theme.typography.pxToRem(18),
      },
    },
    '& li': {
      marginBottom: theme.spacing(4),
    },
    '& li p': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    '& blockquote': {
      marginLeft: 0,
      padding: `${theme.spacing(1)}px 0 ${theme.spacing(1)}px ${theme.spacing(2)}px`,
      borderLeft: `3px solid ${theme.palette.blue.main}`,
    },
    '& blockquote p:last-child': {
      marginBottom: 0,
    },
    '& > ul': {
      listStyleType: 'none',
      paddingLeft: 20,
    },
    '& > ul li': {
      position: 'relative',
    },
    '& > ul li::before': {
      content: "''",
      position: 'absolute',
      left: -20,
      top: 11,
      height: 5,
      width: 5,
      backgroundColor: theme.palette.primary.main,
      display: 'block',
      borderRadius: 100,
    },
    '& > ol': {
      listStyle: 'none',
      counterReset: 'level1',
      paddingLeft: 30,
    },
    '& > ol li': {
      position: 'relative',
    },
    '& > ol li:before': {
      content: "counter(level1) '.'",
      counterIncrement: 'level1',
      position: 'absolute',
      left: -30,
      color: theme.palette.primary.dark,
      fontWeight: 'bold',
      fontSize: 16,
    },
    '& a': {
      textDecoration: 'none',
      boxShadow: `0 2px 0 ${theme.palette.primary.main}`,
      color: theme.palette.grey[600],
      '&:hover': {
        boxShadow: 'none',
        backgroundColor: 'rgba(171,	145,	87, 0.25)',
        color: theme.palette.primary.dark,
      },
    },
  },
  profileTabsDivider: {
    width: '100%',
    opacity: 0.5,
    height: 1,
    backgroundColor: theme.palette.grey.light,
    marginTop: 0,
    marginBottom: theme.spacing(4),
    border: 0,
    flexShrink: 0,
  },
  bioSectionCard: {
    // Bio sits on the page background — no card chrome — per the redesign.
    backgroundColor: 'transparent',
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
    padding: 0,
    '& > $bioAndVideo': {
      marginBottom: theme.spacing(0.5),
    },
    '& $bio': {
      marginBottom: theme.spacing(1),
      color: theme.palette.secondary.main,
      '& p, & ol, & ul': {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
      },
      '& p:first-child': {
        marginTop: 0,
      },
      '& p:last-of-type': {
        marginBottom: 0,
      },
      '& li': {
        marginBottom: theme.spacing(2),
      },
    },
  },
  bioReadMoreButton: {
    /**
     * Inline with the last bio paragraph when that paragraph does not
     * end on a hyperlink — same tertiary underline as the membership
     * banner Read More. `bioReadMoreButtonAfter` is used for Read Less
     * (always after the full bio) and for Read More when a trailing
     * bio `<a>` would otherwise sit on the same line.
     */
    appearance: 'none',
    background: 'transparent',
    border: 0,
    padding: 0,
    margin: 0,
    display: 'inline',
    color: theme.palette.secondary.main,
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 400,
    lineHeight: 'inherit',
    letterSpacing: 'normal',
    textTransform: 'none',
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    cursor: 'pointer',
    '&:hover, &:focus-visible': {
      color: theme.palette.secondary.dark,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.secondary.main}`,
      outlineOffset: 2,
    },
  },
  bioReadMoreButtonAfter: {
    display: 'block',
    marginTop: theme.spacing(1),
  },
  membershipBannerBeforeBio: {
    /**
     * PGA Member box sits under the section nav, before intro video / bio.
     */
    '& > [data-testid="coach-membership-banner"]': {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  introVideoEmbed: {
    width: '100%',
    maxWidth: '100%',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    overflow: 'hidden',
    '& > div': {
      maxWidth: '100%',
    },
  },
  bioCardFooter: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.grey.light}`,
    alignItems: 'center',
    width: '100%',
  },
  followersRow: {
    alignItems: 'center',
    gap: theme.spacing(3),
    justifyContent: 'flex-start',
    textAlign: 'left',
    /**
     * Thin gray vertical dividers between each stat (Followers / Students /
     * Social), mirroring the "Event Details" pattern: gold icon well + two
     * stacked lines of meta, separated from the next item by a 1px rule.
     */
    [theme.breakpoints.up('sm')]: {
      gap: theme.spacing(4),
      '& > * + *': {
        paddingLeft: theme.spacing(4),
        borderLeft: `1px solid ${theme.palette.grey.light}`,
      },
    },
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      textAlign: 'center',
      width: '100%',
    },
  },
  bioStatRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    },
  },
  /**
   * Circular icon background paired with each stat. Mirrors the "Event
   * Details" pattern (gold disc + dark glyph).
   */
  bioStatIconWell: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.gold.main,
    color: theme.palette.secondary.dark,
    '& > svg': {
      width: 20,
      height: 20,
    },
  },
  bioStatBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
    },
  },
  bioStatValue: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '1.1875rem',
    fontWeight: 700,
    lineHeight: 1.1,
    color: theme.palette.grey.darkest,
    letterSpacing: '-0.02em',
  },
  bioStatLabel: {
    fontFamily: theme.typography.overline.fontFamily,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: theme.palette.grey.dark,
    lineHeight: 1.2,
  },
  socialIconInCard: {
    width: 48,
    height: 48,
    marginRight: 0,
    padding: 0,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.blue.main,
    border: '1px solid #dfe3ea',
    borderRadius: '50%',
    boxShadow: 'none',
    outline: 'none',
    overflow: 'hidden',
    '& .MuiSvgIcon-root': {
      fontSize: 24,
      width: 24,
      height: 24,
    },
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.blue.main}`,
      outlineOffset: 3,
    },
    '&:hover, &:focus-visible': {
      backgroundColor: theme.palette.blue.main,
      borderColor: theme.palette.blue.main,
      color: theme.palette.common.white,
      boxShadow: 'none',
      textDecoration: 'none',
    },
  },
  socialIconsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: '0 0 auto',
    padding: 0,
    margin: 0,
    justifyContent: 'flex-end',
    border: 'none',
    boxShadow: 'none',
    outline: 'none',
    minWidth: 0,
    '& > a': {
      marginRight: 0,
    },
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  bioAndVideo: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    /**
     * 24px under the tagline, or under the membership banner when
     * that renders — not the 48px band gap used by Services /
     * Photos / Testimonials / Request Coaching.
     */
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  middleBanner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    backgroundColor: theme.palette.blue.light100,
    padding: '2.5rem 0.5rem',
  },
  contactButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    [theme.breakpoints.only('xs')]: {
      '& > div:first-of-type': {
        flexDirection: 'column',
        alignItems: 'center',
        '& > a': {
          width: '70%',
          marginLeft: 0,
          marginBottom: '1em',
        },
        '& > a:last-of-type': {
          marginBottom: 0,
        },
      },
    },
  },
  profileContactTabPanel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
    padding: 0,
  },
  profileContactSection: {
    ...coachProfileBandSectionGap(theme, { property: 'marginTop' }),
    width: '100%',
    // Keep the Request Coaching heading clear of the sticky site header
    // on native `#coach-profile-contact` hash jumps (`/contact` 302).
    scrollMarginTop: 120,
  },
  contactButtonContainerInTab: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    '& > .MuiContainer-root': {
      maxWidth: '100%',
      paddingLeft: 0,
      paddingRight: 0,
      marginLeft: 0,
      marginRight: 0,
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  },
  externalLinkIcon: {
    marginLeft: '1rem',
    fontSize: '1.5rem',
  },
  backLink: {
    /**
     * Shared uppercase text-button system (lead form overline, "TOOLS I
     * USE", testimonials). `simpleBackLink` restyles color for the white
     * marketplace page.
     */
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: 'rgba(255, 255, 255, 0.85)',
    textDecoration: 'none',
    fontFamily: theme.typography.overline.fontFamily,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    transition: 'color 140ms ease',
    '& svg': {
      marginLeft: -2, // tuck the chevron tight to the label
      transition: 'transform 140ms ease',
    },
    '&:hover, &:focus-visible': {
      color: theme.palette.common.white,
      textDecoration: 'none',
      '& svg': {
        transform: 'translateX(-2px)',
      },
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.gold.main}`,
      outlineOffset: 4,
      borderRadius: 2,
    },
  },
  /**
   * Navy play control on the bottom edge of the header profile photo when the
   * coach has an `introductionVideoUrl`. Opens the intro video dialog.
   */
  heroAvatarVideoBadge: {
    position: 'absolute',
    top: 'auto',
    right: 'auto',
    bottom: 0,
    left: '50%',
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    border: `2px solid ${theme.palette.common.white}`,
    boxShadow: '0 4px 12px rgba(0, 35, 75, 0.32)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    cursor: 'pointer',
    appearance: 'none',
    transform: 'translateX(-50%)',
    transition: 'transform 140ms ease, background-color 140ms ease',
    '&:hover, &:focus-visible': {
      backgroundColor: theme.palette.secondary.light,
      transform: 'translateX(-50%) scale(1.06)',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.common.white}`,
      outlineOffset: 2,
      boxShadow: `0 0 0 4px ${theme.palette.secondary.main}`,
    },
    [theme.breakpoints.up('md')]: {
      width: 48,
      height: 48,
    },
  },
  heroAvatarVideoBadgeIcon: {
    width: 22,
    height: 22,
    marginLeft: 1,
    [theme.breakpoints.up('md')]: {
      width: 28,
      height: 28,
    },
  },
  introVideoDialogPaper: {
    backgroundColor: '#000',
    overflow: 'hidden',
    borderRadius: 12,
    position: 'relative',
  },
  // Visually hidden but exposed to assistive tech and `getByRole` queries.
  // Matches the standard screen-reader-clip pattern used elsewhere in the app.
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  introVideoDialogClose: {
    /**
     * Matches the header desktop navigation drawer's `Close Menu`
     * IconButton: a plain MUI `IconButton` with no surface, border,
     * or shadow — just the `IconClose` glyph centered in the default
     * hit target.
     *
     * Cross-background legibility uses the same classic photo-viewer
     * pattern as the lightbox controls (`coach-photos.lightboxClose`
     * / `lightboxNav`). `mix-blend-mode: difference` was unreliable
     * across the YouTube iframe boundary and the dialog's stacking
     * context, so instead:
     *
     *  - `color: white` so the icon paints white over the black
     *    video paper / any dark video frame
     *  - `& path { stroke-width: 2 }` overrides the `IconClose` 1px
     *    hairline so the X reads cleanly over busy video frames
     *  - `filter: drop-shadow(...)` paints a dark halo around the
     *    strokes so the white X stays readable on light video
     *    preview frames or paused-on-bright-content frames
     *
     * z-index 3 must paint above the Embed wrapper, which sets
     * `zIndex: 2` on its own positioned wrapper (`embed.styles.js`).
     * The close button is a sibling of `DialogContent` (which contains
     * the Embed) inside the Paper, so without a higher z-index the
     * YouTube iframe wrapper paints on top once it mounts and the X
     * disappears the instant the video loads.
     */
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 3,
    color: theme.palette.common.white,
    '& svg': {
      filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.85)) drop-shadow(0 0 4px rgba(0, 0, 0, 0.55))',
    },
    '& path': {
      strokeWidth: 2,
    },
  },
  introVideoDialogContent: {
    padding: 0,
    '&:first-child': {
      paddingTop: 0,
    },
  },
  simplePage: {
    /**
     * Same outer gutters + 1200px canvas as the two-column marketplace
     * row. Nested bands fill the left column rather than full-bleeding
     * below the row. Top padding matches `/coaches/search`
     * (`directoryPageHero`): nav dropdown uses 2.5rem plus a spacing(3)
     * gutter from `md` up, so the page is not flush under global nav.
     */
    ...coachProfileBandOuter(theme, {
      fullBleed: false,
      paddingTop: '2.5rem',
      paddingBottom: theme.spacing(14),
      md: {
        paddingTop: 'calc(2.5rem + 24px)',
        paddingBottom: theme.spacing(6),
      },
    }),
    overflow: 'visible',
    maxWidth: '100%',
    [theme.breakpoints.down('xs')]: {
      overflowX: 'clip',
    },
  },
  contentCanvas: {
    ...coachProfileContentCanvas(),
    overflow: 'visible',
  },
  simpleBackLink: {
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(2.5),
    '&:hover, &:focus-visible': {
      color: theme.palette.blue.main,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.blue.main}`,
    },
  },
  /**
   * Tools & Technology | PGA Accreditations after the membership banner.
   * 12-column MUI Grid (24px gutters via spacing={3}); stacked on
   * xs/sm, two columns from md when both lists have items. Child
   * lists drop their own top margin so the wrapper owns the gap
   * after the banner.
   */
  detailsSection: {
    marginTop: theme.spacing(5),
  },
  detailsHeading: {
    ...coachProfileSectionHeading(theme),
    margin: 0,
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 700,
  },
  detailsColumnHeading: {
    margin: 0,
    marginBottom: theme.spacing(1),
    color: theme.palette.secondary.dark,
    fontWeight: 700,
  },
  detailsLocationList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  detailsLocationItem: {
    marginBottom: theme.spacing(1.25),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  detailsLocationLink: {
    display: 'inline-flex',
    flexDirection: 'column',
    color: theme.palette.secondary.main,
    textDecoration: 'none',
    '&:hover $detailsLocationName, &:focus-visible $detailsLocationName': {
      color: theme.palette.secondary.light,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.blue.main}`,
      outlineOffset: 2,
    },
  },
  detailsLocationName: {
    ...theme.typography.body1,
    color: theme.palette.blue.main,
    fontWeight: 600,
    textDecoration: 'underline',
    textUnderlineOffset: 3,
  },
  detailsLocationAddress: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    whiteSpace: 'pre-line',
    marginTop: 2,
  },
  bioMetaLists: {
    marginTop: theme.spacing(2),
    width: '100%',
    '& > .MuiGrid-item > *': {
      marginTop: 0,
    },
  },
  /**
   * Name uses the `LandingPageHeader` scale (event pages): visual h2
   * rendered as the document h1, without that header's gold underline.
   */
  profileHeader: {
    marginBottom: theme.spacing(0.5),
  },
  profileHeaderIdentity: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(3),
    },
  },
  profileHeaderPhotoWrap: {
    position: 'relative',
    flex: '0 0 auto',
    '& > button': {
      bottom: -12,
    },
  },
  profileHeaderPhoto: {
    width: 88,
    height: 88,
    borderRadius: 16,
    objectFit: 'cover',
    objectPosition: 'top',
    display: 'block',
    [theme.breakpoints.up('md')]: {
      width: 132,
      height: 132,
      borderRadius: 20,
    },
  },
  profileHeaderText: {
    minWidth: 0,
    flex: '1 1 auto',
  },
  profileHeaderName: {
    margin: 0,
    color: theme.palette.secondary.main,
    overflowWrap: 'break-word',
    maxWidth: '100%',
  },
  profileHeaderTitle: {
    margin: 0,
    marginTop: theme.spacing(1),
    color: theme.palette.secondary.main,
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 400,
    lineHeight: 1.4,
    overflowWrap: 'break-word',
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(20),
    },
  },
  profileHeaderVideoButton: {
    marginTop: theme.spacing(1.5),
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    background: 'transparent',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 600,
    color: theme.palette.blue.main,
    '&:hover, &:focus-visible': {
      color: theme.palette.secondary.light,
      textDecoration: 'underline',
      textUnderlineOffset: 3,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.blue.main}`,
      outlineOffset: 2,
    },
  },
  /**
   * Same pill treatment as the FAQ category buttons (`faq.styles.js`).
   * FAQ sits on a light-blue band with white pills; this canvas is white,
   * so the resting fill is inverted to `blue.light25`. Below `sm` the row
   * scrolls horizontally instead of stacking five full-width buttons.
   */
  sectionNav: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
    overflowX: 'auto',
    scrollbarWidth: 'none',
    WebkitOverflowScrolling: 'touch',
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    [theme.breakpoints.up('sm')]: {
      flexWrap: 'wrap',
      overflowX: 'visible',
      marginLeft: 0,
      marginRight: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  sectionNavButton: {
    ...theme.typography.formTags,
    flexShrink: 0,
    height: 45,
    padding: theme.spacing(0, 2.5),
    textAlign: 'center',
    borderRadius: 8,
    whiteSpace: 'nowrap',
    fontSize: 14,
    transition: '250ms',
    backgroundColor: theme.palette.blue.light25,
    color: theme.palette.secondary.main,
    textDecoration: 'none',
    textTransform: 'none',
    '&:focus-visible': {
      outline: '2px solid black',
    },
    '&:hover, &:active': {
      textDecoration: 'none',
      backgroundColor: theme.palette.blue.main,
      color: theme.palette.offwhite.main,
    },
  },
  sectionNavButtonActive: {
    '&&': {
      backgroundColor: theme.palette.blue.main,
      color: theme.palette.offwhite.main,
    },
  },
  sectionNavDivider: {
    width: '100%',
    opacity: 0.5,
    height: 1,
    backgroundColor: theme.palette.grey.light,
  },
  /**
   * Body grid. With the booking widget: 2/3 content + sticky 1/3 sidebar
   * from md; below md the widget stacks between the bio and Services.
   * Without it, content spans the full canvas.
   */
  profileBody: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateAreas: '"header" "intro" "main"',
    overflow: 'visible',
    minWidth: 0,
    maxWidth: '100%',
  },
  profileBodyWithSidebar: {
    gridTemplateAreas: '"header" "sidebar" "intro" "main"',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
      /*
       * Sidebar starts level with the header. A spanning item only grows
       * the flexible track it crosses, so the `1fr` main row absorbs the
       * sidebar height and the header/intro rows stay content-sized.
       */
      gridTemplateAreas: '"header sidebar" "intro sidebar" "main sidebar"',
      gridTemplateRows: 'auto auto 1fr',
      columnGap: theme.spacing(5),
    },
  },
  profileBodyHeader: {
    gridArea: 'header',
    minWidth: 0,
  },
  profileBodyIntro: {
    gridArea: 'intro',
    minWidth: 0,
    /** 24px under the header section nav (BR-018 / BR-019). */
    paddingTop: theme.spacing(3),
  },
  profileBodyMain: {
    gridArea: 'main',
    minWidth: 0,
  },
  profileBodySidebar: {
    gridArea: 'sidebar',
    minWidth: 0,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      position: 'sticky',
      top: 96,
      alignSelf: 'start',
      marginBottom: 0,
    },
  },
  /**
   * Ask the Coach column: below md the lead form stays in the main column.
   * Step 2 can be taller than a short laptop viewport, so the sticky column
   * scrolls on its own instead of leaving Send Request out of reach. The
   * side/bottom padding (offset by negative margins) keeps the card shadow
   * from being clipped by the scroll container.
   */
  profileBodySidebarFromMd: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
      maxHeight: 'calc(100vh - 112px)',
      overflowY: 'auto',
      padding: theme.spacing(0, 2, 3),
      margin: theme.spacing(0, -2),
    },
  },
  sectionNavHiddenFromMd: {
    [theme.breakpoints.up('md')]: {
      '&&': {
        display: 'none',
      },
    },
  },
  sectionNavButtonHiddenFromMd: {
    [theme.breakpoints.up('md')]: {
      '&&': {
        display: 'none',
      },
    },
  },
  profileContactSectionHiddenFromMd: {
    [theme.breakpoints.up('md')]: {
      '&&': {
        display: 'none',
      },
    },
  },
  coachingOptionsCta: {
    width: '100%',
    height: 48,
    minHeight: 48,
    marginTop: theme.spacing(1.5),
    padding: '0 24px',
    borderRadius: 999,
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '0.9375rem',
    boxSizing: 'border-box',
    '&&': {
      backgroundColor: theme.palette.blue.main,
      color: theme.palette.common.white,
      border: `1px solid ${theme.palette.blue.main}`,
      '&:hover, &:focus-visible': {
        backgroundColor: theme.palette.secondary.light,
        borderColor: theme.palette.secondary.light,
        color: theme.palette.common.white,
      },
    },
  },
  contentColumnSocial: {
    ...coachProfileTipSurface(theme),
    width: '100%',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3, 4),
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      justifyContent: 'center',
      gap: theme.spacing(2),
      padding: theme.spacing(2, 1.5),
    },
  },
  socialPromptRow: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 55%',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'center',
    },
  },
  socialPrompt: {
    ...theme.typography.h6,
    color: theme.palette.secondary.dark,
    fontSize: '2rem',
    lineHeight: 1.2,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.125rem',
      textAlign: 'center',
    },
  },
  contactCta: {
    width: '100%',
    height: 48,
    minHeight: 48,
    padding: '0 24px',
    margin: 0,
    borderRadius: 999,
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '0.9375rem',
    boxSizing: 'border-box',
    '&&': {
      backgroundColor: theme.palette.blue.main,
      color: theme.palette.common.white,
      border: `1px solid ${theme.palette.blue.main}`,
      '&:hover': {
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.common.white,
      },
    },
  },
  contactCtaSecondary: {
    '&&': {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.blue.main,
      border: '1px solid #dfe3ea',
      '&:hover': {
        backgroundColor: '#ECF2F9',
        color: theme.palette.blue.main,
        borderColor: theme.palette.blue.main,
      },
    },
  },
  stickyCtaBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))',
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 20,
    alignItems: 'stretch',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
    backgroundColor: theme.palette.common.white,
    borderTop: '1px solid #e8ecf2',
    boxShadow: '0 -8px 24px rgba(15, 34, 52, 0.08)',
    '& a, & button': {
      width: '100%',
      minWidth: 0,
      height: 48,
      boxSizing: 'border-box',
    },
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}))
